/**
 * PausedPanel - Task Paused Status Panel
 *
 * Displays when a task is in 'paused' status, showing:
 * - Paused time, duration, and reason
 * - Previous status and location
 * - Resume button and Update ETA functionality
 * - Quick action buttons for common operations
 */

import React from 'react';
import {
  PauseCircle,
  Play,
  Clock,
  MapPin,
  AlertCircle,
  UserMinus,
  Split,
  XCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../ui/button';
import { ContainerTask } from '../../../types/container';

interface PausedPanelProps {
  task: ContainerTask
  onResume: () => void
  onReassign?: () => void
  onChangeDestination?: () => void
  onChangeDriver?: () => void
  onSplitTask?: () => void
  onCancelTask?: () => void
  isResuming?: boolean
}

// Format duration from minutes to human readable
const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

// Format datetime for display
const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    const month = date.toLocaleString('en-US', { month: 'short' })
    const day = date.getDate()
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
    return `${month} ${day}, ${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`
  } catch {
    return dateString
  }
}

// Calculate duration from pausedAt to now
const calculatePauseDuration = (pausedAt?: string): number => {
  if (!pausedAt) return 0
  try {
    const pausedTime = new Date(pausedAt).getTime()
    const now = Date.now()
    return Math.floor((now - pausedTime) / (1000 * 60)) // minutes
  } catch {
    return 0
  }
}

// Map status key to display label
const getStatusLabel = (status?: string): string => {
  if (!status) return 'Unknown'
  const statusMap: Record<string, string> = {
    'assigned': 'Assigned',
    'picking': 'Picking',
    'delivering': 'Delivering',
    'on-stop': 'On Stop',
    'loading': 'Loading',
    'empty': 'Empty',
    'loaded': 'Loaded',
    'returning': 'Returning'
  }
  return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, ' ')
}

export const PausedPanel: React.FC<PausedPanelProps> = ({
  task,
  onResume,
  onReassign,
  onChangeDestination,
  onChangeDriver,
  onSplitTask,
  onCancelTask,
  isResuming = false
}) => {
  const pauseDuration = task.totalPauseMinutes || calculatePauseDuration(task.pausedAt)
  const previousStatus = task.pausedFromStatus

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-amber-100/50 border-b border-amber-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PauseCircle className="w-5 h-5 text-amber-600" />
          <span className="font-semibold text-amber-800">TASK PAUSED</span>
        </div>
        <div className="flex items-center gap-1.5">
          {onReassign && (
            <button
              onClick={onReassign}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-amber-300 text-amber-700 bg-white/80 hover:bg-amber-50 hover:border-amber-400 transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Reassign
            </button>
          )}
          <button
            onClick={onResume}
            disabled={isResuming}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors ${isResuming ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Play className="w-3 h-3" />
            {isResuming ? 'Resuming...' : 'Resume'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Pause Info Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Clock className="w-3.5 h-3.5" />
              <span>Paused At</span>
            </div>
            <p className="text-sm font-medium text-gray-900">
              {formatDateTime(task.pausedAt)}
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>Previous Status</span>
            </div>
            <p className="text-sm font-medium text-gray-900">
              {getStatusLabel(previousStatus)}
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Clock className="w-3.5 h-3.5" />
              <span>Duration</span>
            </div>
            <p className="text-sm font-medium text-amber-700">
              {formatDuration(pauseDuration)}
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <MapPin className="w-3.5 h-3.5" />
              <span>Location</span>
            </div>
            <p className="text-sm font-medium text-gray-900 truncate" title={task.pauseLocation || task.currentLocation}>
              {task.pauseLocation || task.currentLocation || '-'}
            </p>
          </div>
        </div>

        {/* Reason */}
        {(task.pauseReason || task.pauseReasonDetail) && (
          <div className="bg-white/60 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Reason</div>
            <p className="text-sm text-gray-700">
              {task.pauseReasonDetail || task.pauseReason}
            </p>
          </div>
        )}

        {/* Quick Actions */}
        {(onChangeDestination || onChangeDriver || onSplitTask || onCancelTask) && (
          <div className="border-t border-amber-200 pt-3">
            <div className="text-xs text-gray-500 mb-2">Actions</div>
            <div className="flex flex-wrap gap-2">
              {onChangeDestination && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onChangeDestination}
                  className="gap-1 text-xs"
                >
                  <MapPin className="w-3 h-3" />
                  Change Destination
                </Button>
              )}
              {onChangeDriver && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onChangeDriver}
                  className="gap-1 text-xs"
                >
                  <UserMinus className="w-3 h-3" />
                  Change Driver
                </Button>
              )}
              {onSplitTask && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSplitTask}
                  className="gap-1 text-xs"
                >
                  <Split className="w-3 h-3" />
                  Split Task
                </Button>
              )}
              {onCancelTask && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onCancelTask}
                  className="gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <XCircle className="w-3 h-3" />
                  Cancel Task
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

